import { Capacitor, registerPlugin } from '@capacitor/core';

interface IncomingCallPlugin {
  consumePendingCall(): Promise<{ frame?: string | null }>;
  clearIncomingCall(options: { callId: string }): Promise<void>;
  getFcmToken(): Promise<{ token?: string | null }>;
}

export interface IncomingCallPointer {
  callId: string;
  eventId: string;
  schemaVersion: 2;
}

const IncomingCall = registerPlugin<IncomingCallPlugin>('IncomingCall');

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export async function getPendingNativeCall(): Promise<IncomingCallPointer | null> {
  if (!isNative()) return null;
  try {
    const { frame } = await IncomingCall.consumePendingCall();
    if (typeof frame !== 'string' || !frame.trim()) return null;
    const value = JSON.parse(frame);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record).sort();
    if (keys.length !== 3 || keys.join(',') !== 'callId,eventId,schemaVersion') return null;
    if (
      typeof record.callId !== 'string' || record.callId.length < 1 || record.callId.length > 128
      || typeof record.eventId !== 'string' || record.eventId.length < 1 || record.eventId.length > 128
      || record.schemaVersion !== 2
    ) return null;
    return {
      callId: record.callId,
      eventId: record.eventId,
      schemaVersion: 2,
    };
  } catch {
    return null;
  }
}

export async function clearNativeIncomingCall(callId: string): Promise<void> {
  if (!isNative() || !callId) return;
  try {
    await IncomingCall.clearIncomingCall({ callId });
  } catch {
    // The native alert is best-effort; call state must still resolve in the app.
  }
}

export async function getNativeFcmToken(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const token = (await IncomingCall.getFcmToken()).token?.trim();
    return token || null;
  } catch {
    return null;
  }
}
